import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart } from "lucide-react";

/**
 * Twitter/X-style dramatic Like button:
 *  - Heart bounces (scale 0.7 → 1.35 → 1 with rotational overshoot).
 *  - 6 tiny particles burst radially outward on the "like" transition, fading as they travel.
 *  - Color transitions gray → coral-red with soft glow.
 *  - Number counter has slide-up/slide-down animation on change.
 *  - Haptic feedback (10ms vibration) on mobile via navigator.vibrate.
 *
 * All animations use Framer Motion (already in the bundle) so no new dep is added.
 * The burst only plays on the 0 → 1 transition (like), NOT on unlike, matching X.
 *
 * Props: liked (bool), count (number), onToggle (async fn — should update state).
 */
const PARTICLES = 6;
const PARTICLE_COLORS = ["#E11D48", "#F43F5E", "#FB7185", "#FDA4AF", "#FBBF24", "#F97316"];

export default function LikeButton({
  liked = false,
  count = 0,
  onToggle,
  testid,
  size = 20,
  showCount = true,
  ariaLabel,
}) {
  const [burst, setBurst] = useState(0); // increment triggers a fresh burst animation

  const handleClick = (e) => {
    e.preventDefault();
    // Haptic tap (safe no-op on unsupported browsers/desktop)
    try { if (!liked) navigator.vibrate?.(12); } catch (_) {}
    if (!liked) setBurst((b) => b + 1);
    onToggle?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={liked}
      aria-label={ariaLabel || (liked ? "Batalkan suka" : "Suka postingan")}
      data-testid={testid || "like-button"}
      className={
        "relative inline-flex items-center gap-1.5 min-w-[44px] min-h-[44px] px-2 rounded-full " +
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]/30 " +
        (liked
          ? "text-[#E11D48] hover:bg-[#FEE2E2]"
          : "text-[#4B5563] hover:text-[#E11D48] hover:bg-[#FEF2F2]")
      }
    >
      <span className="relative inline-flex" aria-hidden="true">
        {/* Radiating particle burst — only on like transition */}
        <AnimatePresence>
          {burst > 0 && liked && (
            <Particles key={burst} size={size} />
          )}
        </AnimatePresence>

        {/* Heart with bounce + fill */}
        <motion.span
          initial={false}
          animate={liked
            ? { scale: [0.85, 1.4, 0.9, 1.08, 1], rotate: [0, -8, 6, -3, 0] }
            : { scale: [1.1, 1], rotate: 0 }
          }
          transition={{ duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
          className="inline-flex"
        >
          <Heart
            size={size}
            strokeWidth={1.75}
            fill={liked ? "currentColor" : "none"}
            style={liked ? { filter: "drop-shadow(0 0 6px rgba(225,29,72,0.35))" } : undefined}
            data-testid={testid ? `${testid}-icon` : undefined}
          />
        </motion.span>
      </span>

      {/* Animated counter — slide-up / slide-down on change */}
      {showCount && count > 0 && (
        <span className="relative inline-flex overflow-hidden h-5 items-center text-sm tabular-nums"
          data-testid={testid ? `${testid}-count` : undefined}>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={count}
              initial={{ y: liked ? -14 : 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: liked ? 14 : -14, opacity: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="inline-block"
            >
              {count}
            </motion.span>
          </AnimatePresence>
        </span>
      )}
    </button>
  );
}

function Particles({ size }) {
  const radius = size * 1.1;
  return (
    <span
      data-testid="like-particles"
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-1/2 z-10"
    >
      {Array.from({ length: PARTICLES }).map((_, i) => {
        const angle = (i / PARTICLES) * Math.PI * 2 + Math.random() * 0.35;
        const dist = radius + Math.random() * 4;
        const x = Math.cos(angle) * dist;
        const y = Math.sin(angle) * dist;
        const color = PARTICLE_COLORS[i % PARTICLE_COLORS.length];
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0.4 }}
            animate={{ x, y, opacity: 0, scale: 1.1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "absolute",
              width: 5,
              height: 5,
              borderRadius: 999,
              background: color,
              left: -2,
              top: -2,
              boxShadow: `0 0 4px ${color}`,
            }}
          />
        );
      })}
      {/* Central ring pulse */}
      <motion.span
        initial={{ scale: 0.2, opacity: 0.6 }}
        animate={{ scale: 2.2, opacity: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        style={{
          position: "absolute",
          width: size * 1.4,
          height: size * 1.4,
          borderRadius: 999,
          border: "2px solid #F43F5E",
          left: -(size * 0.7),
          top: -(size * 0.7),
        }}
      />
    </span>
  );
}
